import React, { useState, useRef, useCallback } from 'react';
import { Container, Form, Button, Card, ProgressBar, Alert, Modal } from 'react-bootstrap';
import ErrorMessage from '../components/ErrorMessage';
import { formatError, parseApiError } from '../utils/errorHandling';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { 
  selectUploadStatus, 
  uploadConfigurationStart, 
  uploadConfigurationProgress, 
  uploadConfigurationSuccess, 
  uploadConfigurationFailure,
  clearUploadStatus
} from '../actions/configurationSlice';
import { uploadConfiguration } from '../api/WsaToolsFacade';


const ConfigurationUploadPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { userAlias } = useAppSelector((state) => state.user);
  const uploadStatus = useAppSelector(selectUploadStatus);
  // Using a global marketplace ID since configurations are global
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCloseUploadModal = () => setShowUploadModal(false);
  const handleShowUploadModal = () => setShowUploadModal(true);

  // Validate file type and size
  const validateAndSetFile = useCallback((file: File) => {
    // Check file type (only allow CSV)
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      dispatch(uploadConfigurationFailure('Only CSV files are allowed'));
      return;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      dispatch(uploadConfigurationFailure('File size exceeds 10MB limit'));
      return;
    }

    setFile(file);
    dispatch(clearUploadStatus());
  }, [dispatch]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop event
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  }, [validateAndSetFile]);

  // Trigger file input click
  const onButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      dispatch(uploadConfigurationFailure('Please select a file to upload'));
      return;
    }

    try {
      dispatch(uploadConfigurationStart());
      
      // Read file as base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        try {
          // Extract base64 content (remove data:text/csv;base64, prefix)
          const base64Content = (reader.result as string).split(',')[1];
          
          // Replace spaces with underscores in the filename to avoid issues
          const safeFileName = file.name.replace(/\s+/g, '_');
          
          // Get current username (in a real app this would come from auth context)
          
          // Get current date in YYYY-MM-DD format
          const currentDate = new Date().toISOString().split('T')[0];
          
          // Append username and date to filename (before extension)
          const fileNameParts = safeFileName.split('.');
          const extension = fileNameParts.pop();
          const baseFileName = fileNameParts.join('.');
          const finalFileName = `${baseFileName}_${userAlias}_${currentDate}.${extension}`;
          
          // Upload file with appended username and date
          const response = await uploadConfiguration({
            file: base64Content,
            fileName: finalFileName,
            fileType: 'text/csv'
          });

          // Update progress (this would normally be handled by the onUploadProgress callback)
          dispatch(uploadConfigurationProgress(100));
          
          if (response.status === 200 && response.data.success) {
            dispatch(uploadConfigurationSuccess());
          } else {
            if (response?.data?.errors && Array.isArray(response.data.errors)) {
              // Handle array of errors
              const errorMessages = parseApiError(response, true) as string[];
              dispatch(uploadConfigurationFailure(errorMessages));
            } else {
              // Handle single error message
              const errorMessage = parseApiError(response) as string;
              dispatch(uploadConfigurationFailure(errorMessage));
            }
          }
        } catch (error) {
          dispatch(uploadConfigurationFailure(formatError(error)));
        }
      };
      
      reader.onerror = () => {
        dispatch(uploadConfigurationFailure('Error reading file. Please try again with a different file.'));
      };
    } catch (error) {
      dispatch(uploadConfigurationFailure(formatError(error)));
    }
  };

  return (
    <Container className="mt-4">
      <h2>Configuration Upload</h2>
      <p>Upload a CSV file containing warranty configuration data.</p>
      
      <Form className="mb-4">
        {/* Marketplace selection removed as configurations are global */}
        
        <Card 
          className={`mb-3 ${dragActive ? 'border-primary' : ''}`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <Card.Body className="text-center p-5">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            <div className="mb-3">
              <i className="bi bi-cloud-upload" style={{ fontSize: '3rem' }}></i>
            </div>
            
            <p>Drag and drop a CSV file here, or</p>
            <Button variant="outline-primary" onClick={onButtonClick}>
              Browse Files
            </Button>
            
            {file && (
              <div className="mt-3">
                <strong>Selected file:</strong> {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </Card.Body>
        </Card>
        
        <div className="d-grid gap-2">
          <Button 
            variant="primary" 
            onClick={handleShowUploadModal}
            disabled={!file || uploadStatus.isUploading}
          >
            {uploadStatus.isUploading ? 'Uploading...' : 'Upload Configuration'}
          </Button>
        </div>

        <Modal show={showUploadModal} onHide={handleCloseUploadModal}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Upload</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            I {userAlias} understand that if my configuration file contains BundledBaseUnits, I must submit a SIM to WSA to complete the onboarding.
          </Modal.Body>
          <Modal.Footer>
            <a
              href="https://sim.amazon.com/issues/create?template=dcc2b365-dbbc-4e96-b135-e8452a4281b2"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              Create SIM
            </a>
            <Button variant="secondary" onClick={handleCloseUploadModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => {
              handleUpload();
              handleCloseUploadModal();
            }}>
              Proceed with Upload
            </Button>
          </Modal.Footer>
        </Modal>
      </Form>
      
      {/* Upload Status */}
      {uploadStatus.isUploading && (
        <div className="mb-3">
          <ProgressBar 
            now={uploadStatus.progress} 
            label={`${uploadStatus.progress}%`} 
            animated 
          />
        </div>
      )}
      
      {/* Success Message */}
      {uploadStatus.progress === 100 && !uploadStatus.errors && (
        <Alert variant="success">
          <Alert.Heading>Upload Successful!</Alert.Heading>
          <p>Your configuration file has been uploaded and processed successfully.</p>
        </Alert>
      )}
      
      {/* Error Message */}
      {uploadStatus.errors && (
        <ErrorMessage
          messages={uploadStatus.errors}
          title="Upload Failed"
          onRetry={handleUpload}
          onDismiss={() => dispatch(clearUploadStatus())}
          showRetry={!!file}
          showDismiss={true}
        />
      )}
    </Container>
  );
};

export default ConfigurationUploadPage;
